import { useGetOrganizationContextCapabilities } from "@fp/admin-api-client/organization-context/organization-context.client";
import { useGetOrganizationSettings } from "@fp/admin-api-client/organizations/organizations.client";
import {
  getGetPayeeOrganizationByIdQueryKey,
  getGetPayeeOrganizationPayoutTermsQueryKey,
  getPayeeOrganizationById,
  useAssignPayeeOrganizationPayoutPolicy,
  useDeactivatePayeeOrganization,
  useGetPayeeOrganizationPayoutTerms,
  useReactivatePayeeOrganization,
} from "@fp/admin-api-client/payee-organizations/payee-organizations.client";
import { getListPayoutPoliciesQueryKey, useListPayoutPolicies } from "@fp/admin-api-client/payout-policies/payout-policies.client";
import { Badge } from "@fp/ui/components/base/badges/badges";
import { Button } from "@fp/ui/components/base/buttons/button";
import { PageContentContainer } from "@fp/ui/components/base/page-layout/page-layout";
import { useQueryClient } from "@tanstack/react-query";
import { createFileRoute, useRouter } from "@tanstack/react-router";
import { useCallback } from "react";
import { getErrorMessage } from "@/lib/axios";
import { toast } from "@/lib/toast";
import { AdminLayout } from "@/components/admin-layout";
import { AdminPageHeader } from "@/components/admin-page-header";
import { PayoutTermsAssignment } from "../-components/payout-terms-assignment";

export const Route = createFileRoute("/roster/organizations/$id")({
  loader: async ({ params }) => {
    const payeeOrganization = await getPayeeOrganizationById(params.id);
    return {
      payeeOrganization: payeeOrganization.data,
    };
  },
  component: RouteComponent,
});

function RouteComponent() {
  const { payeeOrganization } = Route.useLoaderData();
  const router = useRouter();
  const queryClient = useQueryClient();
  const { data: capabilitiesResponse } = useGetOrganizationContextCapabilities();
  const { data: orgSettingsResponse } = useGetOrganizationSettings();

  const showsPayoutTerms = orgSettingsResponse?.data?.payoutPolicyEnabled ?? false;
  const canAssignPayoutPolicy = showsPayoutTerms && (capabilitiesResponse?.data?.canAssignTalentPayoutPolicy ?? false);

  const { data: payoutTermsResponse } = useGetPayeeOrganizationPayoutTerms(payeeOrganization.id, {
    query: { queryKey: getGetPayeeOrganizationPayoutTermsQueryKey(payeeOrganization.id), enabled: showsPayoutTerms },
  });
  const { data: payoutPoliciesResponse } = useListPayoutPolicies({ query: { queryKey: getListPayoutPoliciesQueryKey(), enabled: canAssignPayoutPolicy } });
  const assignPayoutPolicyMutation = useAssignPayeeOrganizationPayoutPolicy();
  const deactivateMutation = useDeactivatePayeeOrganization();
  const reactivateMutation = useReactivatePayeeOrganization();
  const payoutTerms = payoutTermsResponse?.data;
  const payoutPolicies = payoutPoliciesResponse?.data ?? [];

  const handleAssignPayoutPolicy = useCallback(
    async (payoutPolicyId: string) => {
      try {
        const response = await assignPayoutPolicyMutation.mutateAsync({
          id: payeeOrganization.id,
          data: { payoutPolicyId },
        });
        queryClient.setQueryData(getGetPayeeOrganizationPayoutTermsQueryKey(payeeOrganization.id), response);
        toast.success(`${payeeOrganization.name} is now on ${response.data.payoutPolicyName ?? "the policy"}'s current terms`);
      } catch (err) {
        toast.error(getErrorMessage(err, "Failed to assign the payout policy"));
      }
    },
    [assignPayoutPolicyMutation, payeeOrganization.id, payeeOrganization.name, queryClient],
  );

  const handleActiveChange = useCallback(async () => {
    try {
      const response = payeeOrganization.isActive
        ? await deactivateMutation.mutateAsync({ id: payeeOrganization.id })
        : await reactivateMutation.mutateAsync({ id: payeeOrganization.id });
      queryClient.setQueryData(getGetPayeeOrganizationByIdQueryKey(payeeOrganization.id), response);
      await router.invalidate();
      toast.success(
        response.data.isActive
          ? `${payeeOrganization.name} can be paid again`
          : `${payeeOrganization.name} will not receive new payouts. Payouts it is already owed still pay.`,
      );
    } catch (err) {
      toast.error(getErrorMessage(err, payeeOrganization.isActive ? "Failed to deactivate the organization" : "Failed to reactivate the organization"));
    }
  }, [deactivateMutation, reactivateMutation, payeeOrganization.id, payeeOrganization.isActive, payeeOrganization.name, queryClient, router]);

  const isChangingActive = deactivateMutation.isPending || reactivateMutation.isPending;

  return (
    <AdminLayout>
      <AdminPageHeader
        title={payeeOrganization.name}
        parent={{ url: "/roster", label: "Roster" }}
      />
      <PageContentContainer className="space-y-6 p-0">
        <div className="px-6 pt-6 pb-6">
          <div className="space-y-3 rounded-lg border border-secondary bg-gray-50 px-4 py-3">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-secondary">
                Payee
              </span>
              <Badge size="sm" color="indigo">
                Organization
              </Badge>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-secondary">
                Paid to
              </span>
              <span className="text-sm text-tertiary">
                {payeeOrganization.name}'s deposit destination, by ACH
              </span>
            </div>
            <div className="flex items-start justify-between gap-4 border-t border-secondary pt-3">
              <div className="flex flex-col gap-0.5">
                <span className="flex items-center gap-2 text-sm font-medium text-secondary">
                  Status
                  <Badge size="sm" color={payeeOrganization.isActive ? "success" : "gray"}>
                    {payeeOrganization.isActive ? "Active" : "Inactive"}
                  </Badge>
                </span>
                <span className="text-sm text-tertiary">
                  {payeeOrganization.isActive
                    ? "On the roster and offered in the direct payout picker"
                    : "Not offered for new payouts. Payouts it is already owed still pay."}
                </span>
              </div>
              <Button
                size="sm"
                color="secondary"
                isDisabled={isChangingActive}
                isLoading={isChangingActive}
                onClick={handleActiveChange}
              >
                {payeeOrganization.isActive ? "Deactivate" : "Reactivate"}
              </Button>
            </div>
            {showsPayoutTerms && payoutTerms && (
              <PayoutTermsAssignment
                payoutTerms={payoutTerms}
                payoutPolicies={payoutPolicies}
                canAssign={canAssignPayoutPolicy}
                isAssigning={assignPayoutPolicyMutation.isPending}
                onAssign={handleAssignPayoutPolicy}
              />
            )}
          </div>
        </div>
      </PageContentContainer>
    </AdminLayout>
  );
}
